import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { collection, getDocs, query, orderBy, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { HiChevronDown, HiChevronRight } from 'react-icons/hi2';
import useAdminAuth from '../../../hooks/useAdminAuth';

const ATTENDANCE_LABELS = { present: '출석', late: '지각', absent: '결석', excused: '사유' };
const ATTENDANCE_COLORS = { present: '#4A7CFE', late: '#F59E0B', absent: '#EF4444', excused: '#9CA3AF' };

const AdminReportsPage = () => {
  const { filterByOwner } = useAdminAuth();
  const [classes, setClasses] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  // Tree state
  const [expandedClasses, setExpandedClasses] = useState({}); // { classId: true/false }
  const [selected, setSelected] = useState(null); // { type: 'class'|'student', id, data, parentClass? }

  // Detail data
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);

  // Load classes and students
  useEffect(() => {
    const load = async () => {
      const [classSnap, userSnap] = await Promise.all([
        getDocs(collection(db, 'classes')),
        getDocs(collection(db, 'users')),
      ]);
      const allClasses = classSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const filtered = filterByOwner(allClasses);
      setClasses(filtered);

      const users = userSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAllUsers(users);
    };
    load();
  }, []);

  // 출석 자동 생성 헬퍼
  const toLocalDateStr = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const autoFillAttendance = async (cls) => {
    const schedules = cls.schedule || [];
    const sIds = cls.studentIds || [];
    if (schedules.length === 0 || sIds.length === 0) return;
    let startDate;
    if (cls.createdAt?.toDate) startDate = cls.createdAt.toDate();
    else if (cls.createdAt) startDate = new Date(cls.createdAt);
    else return;
    startDate.setHours(0, 0, 0, 0);
    const today = toLocalDateStr(new Date());
    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
    const existSnap = await getDocs(collection(db, 'classes', cls.id, 'attendance'));
    const existDates = new Set(existSnap.docs.map(d => d.data().date + '_' + d.data().type));
    for (let d = new Date(startDate); toLocalDateStr(d) < today; d.setDate(d.getDate() + 1)) {
      const dateStr = toLocalDateStr(d);
      const dow = dayNames[d.getDay()];
      if (schedules.some(s => s.day === dow) && !existDates.has(dateStr + '_regular')) {
        const records = {};
        sIds.forEach(sid => { records[sid] = { status: 'present', note: '' }; });
        await addDoc(collection(db, 'classes', cls.id, 'attendance'), { date: dateStr, type: 'regular', records, createdAt: Timestamp.now() });
        existDates.add(dateStr + '_regular');
      }
    }
  };

  // Load detail data when selection changes
  const loadClassDetail = useCallback(async (cls) => {
    setDetailLoading(true);
    try {
      await autoFillAttendance(cls);
      const [attSnap, assignSnap, gradeSnap, matSnap, vidSnap] = await Promise.all([
        getDocs(query(collection(db, 'classes', cls.id, 'attendance'), orderBy('date', 'desc'))),
        getDocs(query(collection(db, 'classes', cls.id, 'assignments'), orderBy('createdAt', 'desc'))),
        getDocs(query(collection(db, 'classes', cls.id, 'grades'), orderBy('createdAt', 'desc'))),
        getDocs(collection(db, 'classes', cls.id, 'materials')),
        getDocs(collection(db, 'classes', cls.id, 'videos')),
      ]);
      const attendance = attSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const assignments = assignSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const grades = gradeSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Attendance summary
      const attCounts = { present: 0, late: 0, absent: 0, excused: 0 };
      let totalRecords = 0;
      attendance.forEach((att) => {
        const records = att.records || {};
        Object.values(records).forEach((r) => {
          const status = typeof r === 'string' ? r : r.status;
          if (attCounts[status] !== undefined) attCounts[status]++;
          totalRecords++;
        });
      });

      // Assignment submission rate
      const studentCount = (cls.studentIds || []).length;
      const assignmentStats = assignments.map((a) => {
        const subs = a.submissions || {};
        const submitted = Object.values(subs).filter((s) => s.status === 'submitted').length;
        return { title: a.title, dueDate: a.dueDate, submitted, total: studentCount };
      });

      // Grade stats
      const gradeStats = grades.map((g) => {
        const scores = Object.values(g.scores || {});
        const avg = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
        return { examName: g.examName, date: g.date, avg, maxScore: g.maxScore || 100, count: scores.length };
      });

      // Overall rates
      const attRate = totalRecords > 0 ? Math.round((attCounts.present / totalRecords) * 100) : 0;
      const totalSubs = assignmentStats.reduce((a, b) => a + b.submitted, 0);
      const totalAssignSlots = assignmentStats.reduce((a, b) => a + b.total, 0);
      const subRate = totalAssignSlots > 0 ? Math.round((totalSubs / totalAssignSlots) * 100) : 0;
      const allAvg = gradeStats.length > 0 ? Math.round(gradeStats.reduce((a, b) => a + b.avg, 0) / gradeStats.length) : 0;

      setDetailData({
        type: 'class',
        summary: { studentCount, attRate, subRate, avgGrade: allAvg, materialCount: matSnap.docs.length, videoCount: vidSnap.docs.length },
        attCounts,
        totalRecords,
        assignmentStats,
        gradeStats,
      });
    } catch (e) {
      console.error(e);
      setDetailData({ type: 'class', error: true });
    }
    setDetailLoading(false);
  }, []);

  const loadStudentDetail = useCallback(async (student, parentClass) => {
    setDetailLoading(true);
    try {
      // parentClass가 있으면 해당 클래스만, 없으면 전체
      const studentClasses = parentClass
        ? [parentClass]
        : classes.filter((c) => (c.studentIds || []).includes(student.id));

      const classDetails = [];
      const calendarEvents = {}; // { 'YYYY-MM-DD': [{ type, label, color }] }
      for (const cls of studentClasses) {
        // 출석 자동 생성
        await autoFillAttendance(cls);

        const [attSnap, assignSnap, gradeSnap] = await Promise.all([
          getDocs(collection(db, 'classes', cls.id, 'attendance')),
          getDocs(collection(db, 'classes', cls.id, 'assignments')),
          getDocs(collection(db, 'classes', cls.id, 'grades')),
        ]);

        // Attendance for this student
        const attCounts = { present: 0, late: 0, absent: 0, excused: 0 };
        let attTotal = 0;
        attSnap.docs.forEach((d) => {
          const data = d.data();
          const records = data.records || {};
          const r = records[student.id];
          if (r) {
            const status = typeof r === 'string' ? r : r.status;
            if (attCounts[status] !== undefined) attCounts[status]++;
            attTotal++;
            if (data.date) {
              if (!calendarEvents[data.date]) calendarEvents[data.date] = [];
              calendarEvents[data.date].push({ type: 'att', status, label: ATTENDANCE_LABELS[status], color: ATTENDANCE_COLORS[status], className: cls.name });
            }
          }
        });

        // Assignments for this student
        let submitted = 0, totalAssign = 0;
        assignSnap.docs.forEach((d) => {
          totalAssign++;
          const data = d.data();
          const subs = data.submissions || {};
          const sub = subs[student.id];
          if (sub?.status === 'submitted') {
            submitted++;
            const subDateObj = sub.submittedAt?.toDate ? sub.submittedAt.toDate() : null;
            const subDate = subDateObj ? `${subDateObj.getFullYear()}-${String(subDateObj.getMonth() + 1).padStart(2, '0')}-${String(subDateObj.getDate()).padStart(2, '0')}` : data.dueDate;
            if (subDate) {
              if (!calendarEvents[subDate]) calendarEvents[subDate] = [];
              calendarEvents[subDate].push({ type: 'assign', label: '과제제출', color: '#22C55E', className: cls.name, title: data.title });
            }
          }
        });

        // Grades for this student
        const gradeList = [];
        gradeSnap.docs.forEach((d) => {
          const data = d.data();
          const score = (data.scores || {})[student.id];
          if (score !== undefined) {
            gradeList.push({ examName: data.examName, date: data.date, score, maxScore: data.maxScore || 100 });
            if (data.date) {
              if (!calendarEvents[data.date]) calendarEvents[data.date] = [];
              calendarEvents[data.date].push({ type: 'grade', label: data.examName, color: '#A855F7', className: cls.name, score });
            }
          }
        });

        classDetails.push({
          classId: cls.id,
          className: cls.name,
          color: cls.color,
          attCounts, attTotal,
          submitted, totalAssign,
          gradeList,
        });
      }

      // Overall summary
      const totalAtt = classDetails.reduce((a, b) => a + b.attTotal, 0);
      const totalPresent = classDetails.reduce((a, b) => a + b.attCounts.present, 0);
      const totalSubmitted = classDetails.reduce((a, b) => a + b.submitted, 0);
      const totalAssignAll = classDetails.reduce((a, b) => a + b.totalAssign, 0);
      const allScores = classDetails.flatMap((c) => c.gradeList.map((g) => g.score));
      const avgScore = allScores.length > 0 ? Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length) : 0;

      setDetailData({
        type: 'student',
        classCount: studentClasses.length,
        attRate: totalAtt > 0 ? Math.round((totalPresent / totalAtt) * 100) : 0,
        subRate: totalAssignAll > 0 ? Math.round((totalSubmitted / totalAssignAll) * 100) : 0,
        avgScore,
        classDetails,
        calendarEvents,
      });
    } catch (e) {
      console.error(e);
      setDetailData({ type: 'student', error: true });
    }
    setDetailLoading(false);
  }, [classes]);

  const handleSelect = (type, item, parentClass) => {
    setSelected({ type, id: item.id, data: item, parentClass });
    if (type === 'class') loadClassDetail(item);
    else loadStudentDetail(item, parentClass);
  };

  // Build user map for names
  const userMap = {};
  allUsers.forEach((u) => { userMap[u.id] = u; });

  const toggleClassExpand = (classId, e) => {
    e.stopPropagation();
    setExpandedClasses((prev) => ({ ...prev, [classId]: !prev[classId] }));
  };

  const totalStudents = new Set(classes.flatMap((c) => c.studentIds || [])).size;

  return (
    <Container>
      <PageHeader>
        <PageTitle>리포트 / 분석</PageTitle>
      </PageHeader>

      <MainLayout>
        {/* Left Tree Panel */}
        <TreePanel>
          {classes.length === 0 && <TreeEmpty>클래스 없음</TreeEmpty>}
          {classes.map((cls) => {
            const classStudents = (cls.studentIds || []).map((id) => userMap[id]).filter(Boolean);
            const isExpanded = expandedClasses[cls.id];
            return (
              <div key={cls.id}>
                <TreeClassItem
                  $active={selected?.type === 'class' && selected?.id === cls.id}
                  onClick={() => handleSelect('class', cls)}
                >
                  <ExpandBtn onClick={(e) => toggleClassExpand(cls.id, e)}>
                    {isExpanded ? <HiChevronDown /> : <HiChevronRight />}
                  </ExpandBtn>
                  <ColorDot $color={cls.color} />
                  <TreeItemName>{cls.name}</TreeItemName>
                  <CategoryCount>{classStudents.length}</CategoryCount>
                </TreeClassItem>
                {isExpanded && (
                  <TreeList>
                    {classStudents.map((s) => (
                      <TreeItem
                        key={s.id}
                        $active={selected?.type === 'student' && selected?.id === s.id && selected?.parentClass?.id === cls.id}
                        onClick={() => handleSelect('student', s, cls)}
                      >
                        <TreeItemName>{s.name}</TreeItemName>
                        {s.school && <TreeItemSub>{s.school} {s.grade}</TreeItemSub>}
                      </TreeItem>
                    ))}
                    {classStudents.length === 0 && <TreeEmpty>학생 없음</TreeEmpty>}
                  </TreeList>
                )}
              </div>
            );
          })}
        </TreePanel>

        {/* Right Detail Panel */}
        <DetailPanel>
          {!selected && <OverallView classes={classes} totalStudents={totalStudents} />}
          {selected && detailLoading && <LoadingMsg>데이터를 불러오는 중...</LoadingMsg>}
          {selected && !detailLoading && detailData?.error && <EmptyMsg>데이터를 불러올 수 없습니다</EmptyMsg>}
          {selected && !detailLoading && detailData && !detailData.error && (
            detailData.type === 'class'
              ? <ClassDetailView cls={selected.data} data={detailData} userMap={userMap} />
              : <StudentDetailView student={selected.data} data={detailData} parentClass={selected.parentClass} />
          )}
        </DetailPanel>
      </MainLayout>
    </Container>
  );
};

/* ===== Overall View ===== */
const OverallView = ({ classes, totalStudents }) => (
  <div>
    <DetailTitle>전체 요약</DetailTitle>
    <SummaryGrid>
      <SummaryCard>
        <SummaryLabel>총 클래스</SummaryLabel>
        <SummaryValue>{classes.length}</SummaryValue>
      </SummaryCard>
      <SummaryCard>
        <SummaryLabel>총 학생</SummaryLabel>
        <SummaryValue>{totalStudents}</SummaryValue>
      </SummaryCard>
    </SummaryGrid>
    <EmptyMsg>왼쪽에서 클래스 또는 학생을 선택하면 상세 통계를 볼 수 있습니다</EmptyMsg>
  </div>
);

/* ===== Class Detail View ===== */
const ClassDetailView = ({ cls, data, userMap }) => {
  const { summary, attCounts, totalRecords, assignmentStats, gradeStats } = data;
  const studentNames = (cls.studentIds || []).map((id) => userMap[id]?.name || id);

  return (
    <div>
      <DetailTitle>
        <ColorDot $color={cls.color} style={{ width: 12, height: 12 }} />
        {cls.name}
      </DetailTitle>
      {cls.schedule?.length > 0 && (
        <ScheduleInfo>
          {cls.schedule.map((s, i) => <ScheduleChip key={i}>{s.day} {s.time}</ScheduleChip>)}
          {cls.room && <ScheduleRoom>{cls.room}</ScheduleRoom>}
        </ScheduleInfo>
      )}

      {/* Summary Cards */}
      <SummaryGrid>
        <SummaryCard>
          <SummaryLabel>학생 수</SummaryLabel>
          <SummaryValue>{summary.studentCount}명</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>출결률</SummaryLabel>
          <SummaryValue>{summary.attRate}%</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>과제 제출률</SummaryLabel>
          <SummaryValue>{summary.subRate}%</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>평균 성적</SummaryLabel>
          <SummaryValue>{summary.avgGrade}점</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>문서</SummaryLabel>
          <SummaryValue>{summary.materialCount}건</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>동영상</SummaryLabel>
          <SummaryValue>{summary.videoCount}건</SummaryValue>
        </SummaryCard>
      </SummaryGrid>

      {/* Attendance Bar */}
      <SectionBlock>
        <SectionLabel>출결 현황</SectionLabel>
        {totalRecords === 0 ? (
          <EmptyMsg>출결 기록이 없습니다</EmptyMsg>
        ) : (
          <>
            <StackedBar>
              {Object.entries(attCounts).map(([status, count]) => {
                const pct = (count / totalRecords) * 100;
                if (pct === 0) return null;
                return <StackedSegment key={status} $color={ATTENDANCE_COLORS[status]} style={{ width: `${pct}%` }} />;
              })}
            </StackedBar>
            <LegendRow>
              {Object.entries(attCounts).map(([status, count]) => (
                <LegendItem key={status}>
                  <LegendDot $color={ATTENDANCE_COLORS[status]} />
                  {ATTENDANCE_LABELS[status]} {count}건 ({totalRecords > 0 ? Math.round((count / totalRecords) * 100) : 0}%)
                </LegendItem>
              ))}
            </LegendRow>
          </>
        )}
      </SectionBlock>

      {/* Assignments */}
      <SectionBlock>
        <SectionLabel>과제 제출 현황</SectionLabel>
        {assignmentStats.length === 0 ? (
          <EmptyMsg>과제가 없습니다</EmptyMsg>
        ) : (
          <DataTable>
            <thead>
              <tr><Th>과제</Th><Th>마감일</Th><Th>제출</Th><Th>제출률</Th></tr>
            </thead>
            <tbody>
              {assignmentStats.map((a, i) => {
                const rate = a.total > 0 ? Math.round((a.submitted / a.total) * 100) : 0;
                return (
                  <tr key={i}>
                    <Td>{a.title}</Td>
                    <Td>{a.dueDate}</Td>
                    <Td>{a.submitted}/{a.total}</Td>
                    <Td><RateBadge $rate={rate}>{rate}%</RateBadge></Td>
                  </tr>
                );
              })}
            </tbody>
          </DataTable>
        )}
      </SectionBlock>

      {/* Grades */}
      <SectionBlock>
        <SectionLabel>성적 현황</SectionLabel>
        {gradeStats.length === 0 ? (
          <EmptyMsg>성적 기록이 없습니다</EmptyMsg>
        ) : (
          <DataTable>
            <thead>
              <tr><Th>시험</Th><Th>날짜</Th><Th>응시</Th><Th>평균</Th></tr>
            </thead>
            <tbody>
              {gradeStats.map((g, i) => (
                <tr key={i}>
                  <Td>{g.examName}</Td>
                  <Td>{g.date}</Td>
                  <Td>{g.count}명</Td>
                  <Td><strong>{g.avg}</strong> / {g.maxScore}</Td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </SectionBlock>
    </div>
  );
};

/* ===== Student Detail View ===== */
const StudentDetailView = ({ student, data, parentClass }) => {
  const { classCount, attRate, subRate, avgScore, classDetails, calendarEvents = {} } = data;
  const now = new Date();
  const [calYear, setCalYear] = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth());

  const prevMonth = () => { if (calMonth === 0) { setCalYear(calYear - 1); setCalMonth(11); } else setCalMonth(calMonth - 1); };
  const nextMonth = () => { if (calMonth === 11) { setCalYear(calYear + 1); setCalMonth(0); } else setCalMonth(calMonth + 1); };

  const firstDay = new Date(calYear, calMonth, 1).getDay();
  const lastDate = new Date(calYear, calMonth + 1, 0).getDate();
  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let i = 1; i <= lastDate; i++) days.push(i);

  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const getDateStr = (d) => `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  // 개설일
  const openDateStr = (() => {
    const c = parentClass?.createdAt;
    if (!c) return null;
    const d = c.toDate ? c.toDate() : new Date(c);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  return (
    <div>
      <DetailTitle>
        {student.name}
        {student.school && <span style={{ fontWeight: 400, fontSize: 14, color: '#6B7280' }}> ({student.school} {student.grade})</span>}
        {parentClass && <span style={{ fontWeight: 500, fontSize: 14, color: '#4A7CFE' }}> — {parentClass.name}</span>}
      </DetailTitle>
      {parentClass?.schedule?.length > 0 && (
        <ScheduleInfo>
          {parentClass.schedule.map((s, i) => <ScheduleChip key={i}>{s.day} {s.time}</ScheduleChip>)}
          {parentClass.room && <ScheduleRoom>{parentClass.room}</ScheduleRoom>}
        </ScheduleInfo>
      )}

      {/* Summary Cards */}
      <SummaryGrid>
        <SummaryCard>
          <SummaryLabel>출결률</SummaryLabel>
          <SummaryValue>{attRate}%</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>과제 제출률</SummaryLabel>
          <SummaryValue>{subRate}%</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>평균 성적</SummaryLabel>
          <SummaryValue>{avgScore}점</SummaryValue>
        </SummaryCard>
      </SummaryGrid>

      {classDetails.length === 0 && <EmptyMsg>수강 중인 클래스가 없습니다</EmptyMsg>}

      {classDetails.map((cd) => (
        <ClassSection key={cd.classId}>
          <ClassSectionTitle>
            <ColorDot $color={cd.color} />
            {cd.className}
          </ClassSectionTitle>

          {/* Attendance */}
          <SubSection>
            <SubSectionLabel>출결</SubSectionLabel>
            {cd.attTotal === 0 ? (
              <EmptyMsgSm>기록 없음</EmptyMsgSm>
            ) : (
              <InlineStats>
                {Object.entries(cd.attCounts).map(([status, count]) => (
                  <StatChip key={status} $color={ATTENDANCE_COLORS[status]}>
                    {ATTENDANCE_LABELS[status]} {count}
                  </StatChip>
                ))}
              </InlineStats>
            )}
          </SubSection>

          {/* Assignments */}
          <SubSection>
            <SubSectionLabel>과제</SubSectionLabel>
            {cd.totalAssign === 0 ? (
              <EmptyMsgSm>과제 없음</EmptyMsgSm>
            ) : (
              <span style={{ fontSize: 14, color: '#374151' }}>
                제출 {cd.submitted}/{cd.totalAssign} ({cd.totalAssign > 0 ? Math.round((cd.submitted / cd.totalAssign) * 100) : 0}%)
              </span>
            )}
          </SubSection>

          {/* Grades */}
          <SubSection>
            <SubSectionLabel>성적</SubSectionLabel>
            {cd.gradeList.length === 0 ? (
              <EmptyMsgSm>성적 없음</EmptyMsgSm>
            ) : (
              <GradeChips>
                {cd.gradeList.map((g, i) => (
                  <GradeChip key={i}>
                    {g.examName}: <strong>{g.score}</strong>/{g.maxScore}
                  </GradeChip>
                ))}
              </GradeChips>
            )}
          </SubSection>
        </ClassSection>
      ))}

      {/* Calendar */}
      <SectionBlock>
        <SectionLabel>활동 달력</SectionLabel>
        <CalendarWrap>
          <CalMonthNav>
            <CalNavBtn onClick={prevMonth}>&lt;</CalNavBtn>
            <CalMonthTitle>{calYear}년 {calMonth + 1}월</CalMonthTitle>
            <CalNavBtn onClick={nextMonth}>&gt;</CalNavBtn>
          </CalMonthNav>
          <CalWeekRow>
            {['일','월','화','수','목','금','토'].map((d, i) => (
              <CalWeekCell key={d} $sun={i === 0} $sat={i === 6}>{d}</CalWeekCell>
            ))}
          </CalWeekRow>
          <CalGrid>
            {days.map((day, i) => {
              if (day === null) return <CalCell key={`e${i}`} />;
              const dateStr = getDateStr(day);
              const col = i % 7;
              const beforeOpen = openDateStr && dateStr < openDateStr;
              if (beforeOpen) return (
                <CalCell key={i} $disabled>
                  <CalDayNum $disabled>{day}</CalDayNum>
                </CalCell>
              );
              const events = calendarEvents[dateStr] || [];
              const isToday = dateStr === todayStr;
              return (
                <CalCell key={i}>
                  <CalDayNum $today={isToday} $sun={col === 0} $sat={col === 6}>{day}</CalDayNum>
                  {events.map((ev, j) => (
                    <CalEventTag key={j} $color={ev.color}>
                      {ev.type === 'att' && ev.label}
                      {ev.type === 'grade' && `${ev.label} ${ev.score}점`}
                      {ev.type === 'assign' && '과제제출'}
                    </CalEventTag>
                  ))}
                </CalCell>
              );
            })}
          </CalGrid>
        </CalendarWrap>
      </SectionBlock>
    </div>
  );
};

export default AdminReportsPage;

/* ===== Styled Components ===== */

const Container = styled.div``;
const PageHeader = styled.div`margin-bottom: 20px;`;
const PageTitle = styled.h1`font-size: 22px; font-weight: 700; color: #111827;`;

const MainLayout = styled.div`
  display: flex; gap: 20px; min-height: 600px;
  @media (max-width: 900px) { flex-direction: column; }
`;

/* Tree Panel */
const TreePanel = styled.div`
  width: 250px; min-width: 250px; background: white; border-radius: 12px;
  border: 1px solid #EAEDF3; padding: 12px 0; overflow-y: auto; max-height: 80vh;
  @media (max-width: 900px) { width: 100%; min-width: 100%; max-height: 300px; }
`;

const TreeClassItem = styled.div`
  display: flex; align-items: center; gap: 6px; padding: 9px 12px;
  cursor: pointer; font-size: 14px; font-weight: 600; color: #374151;
  background: ${({ $active }) => ($active ? '#EBF0FF' : 'transparent')};
  &:hover { background: ${({ $active }) => ($active ? '#EBF0FF' : '#F9FAFB')}; }
`;
const ExpandBtn = styled.span`
  display: flex; align-items: center; cursor: pointer; color: #9CA3AF; font-size: 16px;
`;
const CategoryCount = styled.span`font-size: 11px; color: #9CA3AF; font-weight: 500;`;

const TreeList = styled.div`padding-bottom: 4px;`;
const TreeItem = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 6px 16px 6px 44px;
  cursor: pointer; font-size: 13px; color: #374151;
  background: ${({ $active }) => ($active ? '#EBF0FF' : 'transparent')};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  &:hover { background: ${({ $active }) => ($active ? '#EBF0FF' : '#F9FAFB')}; }
`;
const TreeItemName = styled.span`flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`;
const TreeItemSub = styled.span`font-size: 11px; color: #9CA3AF;`;
const TreeEmpty = styled.div`padding: 12px 32px; font-size: 12px; color: #9CA3AF;`;

const ColorDot = styled.span`
  display: inline-block; width: 8px; height: 8px; border-radius: 50%;
  background: ${({ $color }) => $color || '#9CA3AF'}; flex-shrink: 0;
`;

/* Detail Panel */
const DetailPanel = styled.div`
  flex: 1; background: white; border-radius: 12px;
  border: 1px solid #EAEDF3; padding: 24px; overflow-y: auto; max-height: 80vh;
`;

const DetailTitle = styled.h2`
  font-size: 18px; font-weight: 700; color: #111827; margin-bottom: 8px;
  display: flex; align-items: center; gap: 8px;
`;
const ScheduleInfo = styled.div`display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-bottom:20px;`;
const ScheduleChip = styled.span`font-size:13px;font-weight:600;color:#4A7CFE;background:#EEF2FF;padding:4px 10px;border-radius:6px;`;
const ScheduleRoom = styled.span`font-size:13px;color:#9CA3AF;font-weight:500;`;

const SummaryGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px; margin-bottom: 24px;
`;
const SummaryCard = styled.div`
  background: #F9FAFB; border-radius: 10px; padding: 16px; text-align: center;
`;
const SummaryLabel = styled.div`font-size: 12px; color: #9CA3AF; margin-bottom: 4px;`;
const SummaryValue = styled.div`font-size: 24px; font-weight: 800; color: #111827;`;

const SectionBlock = styled.div`margin-bottom: 24px;`;
const SectionLabel = styled.h3`font-size: 15px; font-weight: 700; color: #374151; margin-bottom: 12px;`;

/* Stacked Bar */
const StackedBar = styled.div`
  display: flex; height: 24px; border-radius: 12px; overflow: hidden; background: #F3F4F6;
`;
const StackedSegment = styled.div`
  height: 100%; background: ${({ $color }) => $color}; transition: width 0.3s;
`;
const LegendRow = styled.div`display: flex; gap: 16px; flex-wrap: wrap; margin-top: 8px;`;
const LegendItem = styled.span`display: flex; align-items: center; gap: 4px; font-size: 12px; color: #6B7280;`;
const LegendDot = styled.span`width: 8px; height: 8px; border-radius: 50%; background: ${({ $color }) => $color};`;

/* Table */
const DataTable = styled.table`width: 100%; border-collapse: collapse; font-size: 13px;`;
const Th = styled.th`text-align: left; padding: 10px 12px; font-size: 12px; font-weight: 600; color: #9CA3AF; background: #F9FAFB; border-bottom: 1px solid #EAEDF3;`;
const Td = styled.td`padding: 10px 12px; color: #374151; border-bottom: 1px solid #F3F4F6; strong { font-weight: 700; }`;

const RateBadge = styled.span`
  display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 12px; font-weight: 600;
  background: ${({ $rate }) => ($rate >= 80 ? '#ECFDF5' : $rate >= 50 ? '#FEF9C3' : '#FEE2E2')};
  color: ${({ $rate }) => ($rate >= 80 ? '#059669' : $rate >= 50 ? '#CA8A04' : '#DC2626')};
`;

/* Student detail - class sections */
const ClassSection = styled.div`
  background: #F9FAFB; border-radius: 10px; padding: 16px; margin-bottom: 12px;
`;
const ClassSectionTitle = styled.div`
  display: flex; align-items: center; gap: 8px; font-size: 15px; font-weight: 700;
  color: #111827; margin-bottom: 12px;
`;
const SubSection = styled.div`margin-bottom: 10px; &:last-child { margin-bottom: 0; }`;
const SubSectionLabel = styled.div`font-size: 12px; font-weight: 600; color: #9CA3AF; margin-bottom: 4px;`;

const InlineStats = styled.div`display: flex; gap: 8px; flex-wrap: wrap;`;
const StatChip = styled.span`
  display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 10px;
  font-size: 12px; font-weight: 600;
  background: ${({ $color }) => $color}18; color: ${({ $color }) => $color};
`;

const GradeChips = styled.div`display: flex; gap: 8px; flex-wrap: wrap;`;
const GradeChip = styled.span`
  padding: 4px 10px; border-radius: 8px; font-size: 13px; color: #374151;
  background: white; border: 1px solid #E5E7EB;
`;

const LoadingMsg = styled.div`padding: 40px; text-align: center; color: #9CA3AF; font-size: 14px;`;
const EmptyMsg = styled.div`padding: 24px; text-align: center; color: #9CA3AF; font-size: 14px;`;
const EmptyMsgSm = styled.span`font-size: 12px; color: #9CA3AF;`;

/* Calendar */
const CalendarWrap = styled.div`background: #F9FAFB; border-radius: 12px; padding: 20px;`;
const CalMonthNav = styled.div`display: flex; align-items: center; justify-content: center; gap: 20px; margin-bottom: 16px;`;
const CalNavBtn = styled.button`background: none; border: none; font-size: 18px; color: #6B7280; cursor: pointer; padding: 4px 8px; border-radius: 6px; &:hover { background: #E5E7EB; }`;
const CalMonthTitle = styled.span`font-size: 16px; font-weight: 700; color: #111827; min-width: 120px; text-align: center;`;
const CalWeekRow = styled.div`display: grid; grid-template-columns: repeat(7, 1fr); margin-bottom: 4px;`;
const CalWeekCell = styled.div`text-align: center; font-size: 13px; font-weight: 700; padding: 8px 0; color: ${({ $sun, $sat }) => $sun ? '#EF4444' : $sat ? '#4A7CFE' : '#9CA3AF'};`;
const CalGrid = styled.div`display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px;`;
const CalCell = styled.div`
  min-height: 80px; padding: 6px 4px; display: flex; flex-direction: column; align-items: center;
  border: 1px solid #F3F4F6; border-radius: 6px;
  ${({ $disabled }) => $disabled && 'background:#FAFAFA;opacity:0.4;'}
`;
const CalDayNum = styled.span`
  font-size: 14px; font-weight: 600; margin-bottom: 4px;
  color: ${({ $disabled, $sun, $sat, $today }) => $disabled ? '#D1D5DB' : $today ? 'white' : $sun ? '#EF4444' : $sat ? '#4A7CFE' : '#374151'};
  ${({ $today }) => $today && `
    display: inline-flex; align-items: center; justify-content: center;
    background: #4A7CFE; border-radius: 50%; width: 26px; height: 26px;
  `}
`;
const CalEventTag = styled.div`
  font-size: 11px; font-weight: 600; padding: 1px 6px; border-radius: 4px; margin-top: 2px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%;
  background: ${({ $color }) => $color}18; color: ${({ $color }) => $color};
`;
